import { useState } from "react";
import { Smartphone, Mail, Copy, Download, Loader2, ShieldCheck, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../context/LangContext";

export const codeCls = "w-full rounded-xl border border-[#120E1F]/10 bg-white px-4 py-3 text-center font-mono text-xl tracking-[0.4em] focus:outline-none focus:border-[#8A5CD6]";

export const pwScore = (pw) => [pw.length >= 8, /[A-Za-z]/.test(pw) && /\d/.test(pw), pw.length >= 12, /[^A-Za-z0-9]/.test(pw)].filter(Boolean).length;

export const PasswordStrength = ({ pw }) => {
  const { tr } = useLang();
  if (!pw) return null;
  const s = pwScore(pw);
  const labels = [tr("Zu schwach", "Too weak"), tr("Schwach", "Weak"), tr("Okay", "Okay"), tr("Stark", "Strong"), tr("Sehr stark", "Very strong")];
  const colors = ["#E0457B", "#E0457B", "#F2C230", "#8A5CD6", "#2E9E6B"];
  return (
    <div className="space-y-1" data-testid="password-strength">
      <div className="flex gap-1">{[0, 1, 2, 3].map((i) => <span key={i} className="h-1.5 flex-1 rounded-full transition-colors" style={{ background: i < s ? colors[s] : "#120E1F1A" }} />)}</div>
      <p className="text-[11px]" style={{ color: colors[s] }}>{labels[s]} · {tr("min. 8 Zeichen mit Buchstaben und Zahlen", "min. 8 chars with letters and numbers")}</p>
    </div>
  );
};

export const TrustRow = ({ dark = false }) => {
  const { tr } = useLang();
  const items = [[Lock, tr("SSL-verschlüsselt", "SSL encrypted")], [ShieldCheck, tr("Zwei-Faktor-Schutz", "Two-factor protection")], [KeyRound, tr("Passwörter sicher gehasht", "Passwords securely hashed")]];
  return (
    <div className={`flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] ${dark ? "text-white/60" : "text-[#707783]"}`} data-testid="trust-row">
      {items.map(([I, l]) => <span key={l} className="flex items-center gap-1.5"><I className="h-3.5 w-3.5 text-[#2E9E6B]" />{l}</span>)}
    </div>
  );
};

export const BackupCodes = ({ codes, onDone }) => {
  const { tr } = useLang();
  const text = codes.join("\n");
  const download = () => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([`Jolgen Cards – Backup-Codes\n\n${text}\n`], { type: "text/plain" })); a.download = "jolgen-backup-codes.txt"; a.click(); };
  return (
    <div className="space-y-4" data-testid="backup-codes">
      <p className="text-sm text-[#120E1F]"><strong>{tr("Speichere deine Backup-Codes!", "Save your backup codes!")}</strong> {tr("Jeder Code funktioniert einmal, falls du keinen Zugriff auf deine 2FA hast. Sie werden nur jetzt angezeigt.", "Each code works once if you lose access to your 2FA. They are only shown now.")}</p>
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#120E1F] p-4">{codes.map((c) => <code key={c} className="font-mono text-sm text-[#F2C230] text-center" data-testid="backup-code">{c}</code>)}</div>
      <div className="flex flex-wrap gap-3">
        <button onClick={() => { navigator.clipboard.writeText(text); toast.success(tr("Kopiert", "Copied")); }} className="btn-outline" data-testid="copy-backup-codes"><Copy className="h-4 w-4" />{tr("Kopieren", "Copy")}</button>
        <button onClick={download} className="btn-outline" data-testid="download-backup-codes"><Download className="h-4 w-4" />{tr("Herunterladen", "Download")}</button>
        {onDone && <button onClick={onDone} className="btn-dark ml-auto" data-testid="backup-codes-done">{tr("Gespeichert, weiter", "Saved, continue")}</button>}
      </div>
    </div>
  );
};

export const TwoFactorSetup = ({ onEnabled }) => {
  const { tr } = useLang();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const start = async (method) => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/start", { method }); setSetup(data); setCode(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const confirm = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/2fa/confirm", { code }); setCodes(data.backup_codes); toast.success(tr("2FA aktiviert", "2FA enabled")); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (codes) return <BackupCodes codes={codes} onDone={onEnabled} />;
  if (!setup) return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="twofa-method-choice">
      {[["totp", Smartphone, tr("Authenticator-App", "Authenticator app"), tr("Google Authenticator, Authy, 1Password … am sichersten", "Google Authenticator, Authy, 1Password … most secure")],
        ["email", Mail, tr("Code per E-Mail", "Code by email"), tr("6-stelliger Code bei jeder Anmeldung", "6-digit code at every sign-in")]].map(([m, I, t, s]) => (
        <button key={m} disabled={busy} onClick={() => start(m)} data-testid={`twofa-choose-${m}`} className="text-left rounded-2xl border border-[#120E1F]/10 p-4 hover:border-[#8A5CD6] hover:bg-[#8A5CD6]/5 transition-colors">
          <I className="h-5 w-5 text-[#6E3FB8]" /><p className="font-semibold mt-2">{t}</p><p className="text-xs text-[#707783]">{s}</p>
        </button>
      ))}
    </div>
  );
  return (
    <form onSubmit={confirm} className="space-y-4" data-testid="twofa-confirm-form">
      {setup.method === "totp" ? (
        <div className="flex flex-col sm:flex-row gap-5 items-center">
          <img src={setup.qr} alt="QR" className="h-44 w-44 rounded-xl border" data-testid="twofa-qr" />
          <div className="text-sm space-y-2"><p>{tr("1. Scanne den QR-Code mit deiner Authenticator-App.", "1. Scan the QR code with your authenticator app.")}</p>
            <p className="text-xs text-[#707783]">{tr("Oder gib den Schlüssel manuell ein:", "Or enter the key manually:")}</p>
            <code className="block break-all font-mono text-xs bg-[#F7F5FB] rounded-lg p-2" data-testid="twofa-secret">{setup.secret}</code>
            <p>{tr("2. Gib den 6-stelligen Code aus der App ein.", "2. Enter the 6-digit code from the app.")}</p></div>
        </div>
      ) : <p className="text-sm">{tr("Wir haben einen 6-stelligen Code an", "We sent a 6-digit code to")} <strong>{setup.email_hint}</strong> {tr("gesendet.", ".")}</p>}
      <input required inputMode="numeric" pattern="\d{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className={codeCls} data-testid="twofa-setup-code-input" />
      <div className="flex gap-3"><button type="button" onClick={() => setSetup(null)} className="btn-outline">{tr("Zurück", "Back")}</button>
        <button disabled={busy || code.length !== 6} className="btn-dark flex-1" data-testid="twofa-setup-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("Aktivieren", "Enable")}</button></div>
    </form>
  );
};
